import { Alert, Box, Icon, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { Salary } from 'modules/job_catalog/lib/salary'
import { formatSalaryFromCents, isValidSalaryValue } from 'modules/job_catalog/lib/salaryUtils'

interface Props {
  precedenceSalaryRange: Salary
  salaryAmount: number
  salaryFrequency?: string
  roleLevelName: string
  legalEntityCountry: string
  locationId?: string
}

const translateTimeUnit = (timeUnit: string) => {
  switch (timeUnit) {
    case 'hourly':
      return i18n.t(
        'add_employee_modal.contract_information_step.callouts.salary_range.time_unit.hourly'
      )
    case 'daily':
      return i18n.t(
        'add_employee_modal.contract_information_step.callouts.salary_range.time_unit.daily'
      )
    case 'weekly':
      return i18n.t(
        'add_employee_modal.contract_information_step.callouts.salary_range.time_unit.weekly'
      )
    case 'monthly':
      return i18n.t(
        'add_employee_modal.contract_information_step.callouts.salary_range.time_unit.monthly'
      )
    case 'yearly':
      return i18n.t(
        'add_employee_modal.contract_information_step.callouts.salary_range.time_unit.yearly'
      )
  }
}

const SalaryInRangeCallout = ({
  precedenceSalaryRange,
  salaryAmount,
  salaryFrequency,
  roleLevelName,
  legalEntityCountry,
  locationId,
}: Props) => {
  const isValidSalary = isValidSalaryValue(
    precedenceSalaryRange,
    salaryAmount,
    salaryFrequency || ''
  )

  const title = isValidSalary
    ? i18n.t('add_employee_modal.contract_information_step.callouts.salary_range.title.match')
    : i18n.t('add_employee_modal.contract_information_step.callouts.salary_range.title.not_match')

  const workplaceDescription = (() => {
    if (
      !locationId ||
      !precedenceSalaryRange.workplaces ||
      precedenceSalaryRange.workplaces.length === 0
    )
      return ''

    let workplaceName: string | undefined

    if (precedenceSalaryRange.workplaces.length === 1) {
      workplaceName = precedenceSalaryRange.workplaces[0]?.name
    } else {
      const matchingWorkplace = precedenceSalaryRange.workplaces.find(
        (workplace) => workplace.id === locationId
      )
      workplaceName = matchingWorkplace?.name
    }

    return workplaceName
  })()

  const bodyText = (() => {
    const timeUnit = translateTimeUnit(precedenceSalaryRange.periodicity)

    if (precedenceSalaryRange.gross) {
      if (workplaceDescription) {
        return i18n.t(
          'add_employee_modal.contract_information_step.callouts.salary_range.message.fixed',
          {
            name: roleLevelName,
            workplaces: workplaceDescription,
            amount: formatSalaryFromCents(
              precedenceSalaryRange.gross,
              legalEntityCountry,
              precedenceSalaryRange.currency
            ),
            timeUnit: timeUnit,
          }
        )
      } else {
        return i18n.t(
          'add_employee_modal.contract_information_step.callouts.salary_range.message.fixed_without_workplace',
          {
            name: roleLevelName,
            amount: formatSalaryFromCents(
              precedenceSalaryRange.gross,
              legalEntityCountry,
              precedenceSalaryRange.currency
            ),
            timeUnit: timeUnit,
          }
        )
      }
    }

    if (precedenceSalaryRange.min && precedenceSalaryRange.max) {
      if (workplaceDescription) {
        return i18n.t(
          'add_employee_modal.contract_information_step.callouts.salary_range.message.range',
          {
            name: roleLevelName,
            workplaces: workplaceDescription,
            min: formatSalaryFromCents(
              precedenceSalaryRange.min,
              legalEntityCountry,
              precedenceSalaryRange.currency
            ),
            max: formatSalaryFromCents(
              precedenceSalaryRange.max,
              legalEntityCountry,
              precedenceSalaryRange.currency
            ),
            timeUnit: timeUnit,
          }
        )
      } else {
        return i18n.t(
          'add_employee_modal.contract_information_step.callouts.salary_range.message.range_without_workplace',
          {
            name: roleLevelName,
            min: formatSalaryFromCents(
              precedenceSalaryRange.min,
              legalEntityCountry,
              precedenceSalaryRange.currency
            ),
            max: formatSalaryFromCents(
              precedenceSalaryRange.max,
              legalEntityCountry,
              precedenceSalaryRange.currency
            ),
            timeUnit: timeUnit,
          }
        )
      }
    }
    return ''
  })()

  return (
    <Box paddingTop="s20">
      <Alert color={isValidSalary ? 'info' : 'warning'}>
        <Box gap="s8">
          <Box flexDirection="row" gap="s4">
            {isValidSalary ? (
              <Icon.Medium icon="link" color="group1" />
            ) : (
              <Icon.Medium icon="link-remove" color="group4" />
            )}
            <Text.Normal size="200" weight="bold">
              {title}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" gap="s4">
            {bodyText}
          </Box>
        </Box>
      </Alert>
    </Box>
  )
}

export default SalaryInRangeCallout
